
import { ServiceVisit, ServiceFrequency, PlantRange } from '@/types';
import { format, addWeeks, addDays } from 'date-fns';

export interface CalendarEvent {
  title: string;
  description: string;
  startDate: Date;
  endDate: Date;
  location?: string;
}

// Function to convert a ServiceVisit into a CalendarEvent
export const visitToEvent = (visit: ServiceVisit): CalendarEvent => {
  // Estimate the visit duration (30 minutes for 1-10 plants, add 15 minutes for each range increase)
  const durationInMinutes = visit.plantCount === PlantRange.Range1 ? 30 : 
                           visit.plantCount === PlantRange.Range2 ? 45 :
                           visit.plantCount === PlantRange.Range3 ? 60 : 75;
  
  // Create a shallow copy of the visit date to avoid mutations
  const startDate = new Date(visit.date);
  
  // If we have a time slot, set appropriate time
  if (visit.timeSlot === 'morning') {
    startDate.setHours(9, 0, 0, 0); // 9:00 AM
  } else {
    startDate.setHours(13, 0, 0, 0); // 1:00 PM
  }
  
  // Calculate end time by adding duration
  const endDate = new Date(startDate.getTime() + durationInMinutes * 60 * 1000);
  
  return {
    title: `Plant Care: ${visit.serviceType}`,
    description: `Plant care service for ${visit.plantCount} plants. ${visit.technicianName ? `Technician: ${visit.technicianName}` : ''}`,
    startDate,
    endDate,
    location: visit.address
  };
};

// Generate recurring events if needed
export const generateRecurringEvents = (
  baseEvent: CalendarEvent,
  frequency: ServiceFrequency,
  recurrenceCount: number = 10 // Default to 10 occurrences
): CalendarEvent[] => {
  const events = [baseEvent];
  
  if (frequency === ServiceFrequency.OneTime) {
    return events;
  }
  
  // Clone the base event
  const cloneEvent = (date: Date): CalendarEvent => {
    const startDate = new Date(date);
    const endDate = new Date(date.getTime() + (baseEvent.endDate.getTime() - baseEvent.startDate.getTime()));
    
    return {
      ...baseEvent,
      startDate,
      endDate
    };
  };
  
  // Create recurring events
  for (let i = 1; i < recurrenceCount; i++) {
    let nextDate;
    
    switch (frequency) {
      case ServiceFrequency.Weekly:
        nextDate = addWeeks(baseEvent.startDate, i);
        break;
      case ServiceFrequency.BiWeekly:
      case ServiceFrequency.BiWeeklyRecurring:
        nextDate = addWeeks(baseEvent.startDate, i * 2);
        break;
      default:
        continue;
    }
    
    events.push(cloneEvent(nextDate));
  }
  
  return events;
};

// Generate iCalendar format string
export const generateICalString = (events: CalendarEvent[]): string => {
  const formatDate = (date: Date) => {
    return format(date, "yyyyMMdd'T'HHmmss");
  };
  
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Plant Autopilot//Calendar//EN'
  ];
  
  events.forEach(event => {
    lines.push(
      'BEGIN:VEVENT',
      `SUMMARY:${event.title}`,
      `DESCRIPTION:${event.description.replace(/\n/g, '\\n')}`,
      `DTSTART:${formatDate(event.startDate)}`,
      `DTEND:${formatDate(event.endDate)}`,
      event.location ? `LOCATION:${event.location}` : '',
      `UID:${Date.now()}-${Math.floor(Math.random() * 1000000)}@plantautopilot.app`,
      'END:VEVENT'
    );
  });
  
  lines.push('END:VCALENDAR');
  
  return lines.filter(Boolean).join('\r\n');
};

// Download calendar file
export const downloadCalendarFile = (
  events: CalendarEvent[],
  filename: string = 'plant-care-schedule.ics'
): void => {
  const icalString = generateICalString(events);
  const blob = new Blob([icalString], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Download a single visit as calendar file
export const downloadVisitCalendar = (visit: ServiceVisit) => {
  const event = visitToEvent(visit);
  downloadCalendarFile([event], `plant-care-${format(visit.date, 'yyyy-MM-dd')}.ics`);
};

// Download multiple visits as a single calendar file
export const downloadVisitsCalendar = (visits: ServiceVisit[]) => {
  const events = visits.map(visitToEvent);
  downloadCalendarFile(events, 'plant-care-schedule.ics');
};
